package dev.lingvo.site.components.release

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.ReleaseCheckReportDto
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.state.ProjectActionKey
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.actionKey
import dev.lingvo.site.state.requestProjectResource
import kotlinx.coroutines.CancellationException
import org.jetbrains.compose.web.css.FlexWrap

private class ReviewedReleaseState {
    var loading by mutableStateOf(false)
    var report by mutableStateOf<ReleaseCheckReportDto?>(null)
    var failure by mutableStateOf<Throwable?>(null)
}

private data class ReviewedReleaseIntent(val sequence: Int, val locale: String)

/** Explicit point-in-time hosted policy; ordinary readiness and downloads have their own policy. */
@Composable
internal fun ReviewedCurrentReleaseCheck(
    project: ProjectDetailDto,
    authority: ProjectAuthority,
    actionKey: ProjectActionKey,
    currentRoute: () -> VerifiedProjectRoute?,
    currentMemberships: () -> List<WorkspaceDto>,
    onNavigate: (String) -> Unit,
) {
    if (!authority.canReadProject) return
    val supported = Session.serverFeatures.reviewedCurrentReleaseChecks
    val targets = project.locales.filterNot { it == project.sourceLocale }
    var locale by remember(actionKey) { mutableStateOf("") }
    val renderedLocale = locale
    val state = remember(actionKey, authority.role, locale, supported, project.sourceLocale, project.locales) { ReviewedReleaseState() }
    // A native change and click can arrive before composition. Keep intent outside locale-keyed
    // presentation so that valid click targets the latest selected value rather than disappearing.
    var intent by remember(actionKey, authority.role, supported, project.sourceLocale, project.locales) {
        mutableStateOf<ReviewedReleaseIntent?>(null)
    }
    var consumedSequence by remember(actionKey, authority.role, supported, project.sourceLocale, project.locales) { mutableStateOf(0) }
    val liveRoute by rememberUpdatedState(currentRoute)
    val liveMemberships by rememberUpdatedState(currentMemberships)
    fun admissionCurrent(): Boolean {
        val route = liveRoute()?.takeIf { it.actionKey() == actionKey } ?: return false
        return Session.serverFeatures.reviewedCurrentReleaseChecks &&
            ProjectAuthority.resolve(route.project, liveMemberships()).role == authority.role
    }

    fun remainsCurrent(request: ReviewedReleaseIntent): Boolean = admissionCurrent() &&
        locale == request.locale && intent == request

    LaunchedEffect(state, intent) {
        val request = intent ?: return@LaunchedEffect
        if (request.sequence <= consumedSequence || !supported || !remainsCurrent(request)) return@LaunchedEffect
        consumedSequence = request.sequence
        state.loading = true
        state.report = null
        state.failure = null
        try {
            val report = requestProjectResource(actionKey, project.slug, liveMemberships(), liveRoute, liveMemberships) { route ->
                ApiClient.reviewedCurrentReleaseCheck(route.project.slug, request.locale.ifEmpty { null })
            }
            if (remainsCurrent(request)) state.report = report
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: Throwable) {
            if (remainsCurrent(request)) state.failure = failure
        } finally {
            if (remainsCurrent(request)) state.loading = false
        }
    }

    LCard(Modifier.fillMaxWidth().attr("data-reviewed-release-check", "true")) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            LSectionTitle(t("release.reviewed_current_release_check.optional_source_and_approval_check", "Optional source and approval check"))
            LMuted(t("release.reviewed_current_release_check.policy_description", "Check that each translation is approved against the current source, with no blocking QA, review or unanswered questions."))
            LMuted(t("release.reviewed_current_release_check.scope_limits", "This checks hosted project data at the stated time. It does not certify your checkout or future downloads. Ordinary readiness and export settings stay unchanged."))
            when {
                !supported -> {
                    LSectionTitle(t("release.reviewed_current_release_check.optional_check_unavailable", "Optional check unavailable"))
                    LMuted(t("release.reviewed_current_release_check.service_support_required", "This service has not advertised the optional check. Ordinary readiness cannot substitute for its result."))
                }
                targets.isEmpty() -> LMuted(if (authority.canEditStructure) {
                    t("release.reviewed_current_release_check.no_target_scope", "Enable a target language in project settings before checking its approval and source basis.")
                } else {
                    t("release.reviewed_current_release_check.no_target_scope_reader", "A project owner or admin can enable a target language. There is no target scope to check yet.")
                })
                else -> {
                    Row(Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap).gap(Space.MD)) {
                        LSelect(locale, listOf(LSelectOption("", t("release.reviewed_current_release_check.all_target_languages", "All target languages"))) +
                            targets.map { LSelectOption(it, it) }, onValueChange = { locale = it },
                            label = t("release.reviewed_current_release_check.languages_to_check", "Languages to check"),
                            modifier = Modifier.attr("data-reviewed-release-locale", "true"))
                        LButton(
                            label = when {
                                state.failure != null -> t("release.reviewed_current_release_check.retry_optional_check", "Retry optional check")
                                state.report != null -> t("release.reviewed_current_release_check.check_again", "Check again")
                                else -> t("release.reviewed_current_release_check.run_optional_check", "Run optional check")
                            },
                            onClick = {
                                if (admissionCurrent()) intent = ReviewedReleaseIntent((intent?.sequence ?: 0) + 1, locale)
                            }, loading = state.loading,
                            variant = LButtonVariant.SECONDARY, size = LButtonSize.SM,
                            enabled = !state.loading && (locale.isEmpty() || locale in targets),
                        )
                    }
                    ReviewedCurrentReleaseStatus(state.report)
                    state.failure?.let { failure ->
                        val unavailable = (failure as? ApiException)?.status in listOf(404, 405, 501)
                        LAlert(
                            title = if (unavailable) t("release.reviewed_current_release_check.optional_check_unavailable", "Optional check unavailable")
                                else t("release.reviewed_current_release_check.couldnt_check", "Could not complete the optional check"),
                            description = failure.message,
                            variant = LAlertVariant.WARNING, live = true,
                        )
                    }
                    state.report?.let { report ->
                        ReviewedCurrentReleaseResult(report, authority, onNavigate = { path ->
                            if (admissionCurrent() && locale == renderedLocale && state.report == report) onNavigate(path)
                        })
                    }
                }
            }
        }
    }
}
